import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import {
  getBinaryValue,
  getSorting,
  mergeOrgStructureIds,
} from 'utils/general';
import {
  IBaseSurvey,
  ISurvey,
  ISurveyGroupAssignment,
  ISurveyResults,
  IBaseSurveySubmission,
  ISurveySubmission,
  IBaseSurveyPendingSubmission,
} from 'types/income';
import { Translations } from 'types/Common';
import { ContentLanguage, TableDataResponse } from 'types/general';
import {
  NotificationMethod,
  SurveyAnswerType,
  SurveyChoiceAnswerFormat,
  SurveyChoiceAnswerType,
  SurveyPendingSubmissionsFilters,
  SurveysFilters,
  SurveySubmissionsFilters,
  SurveyType,
  TrainingDifficulty,
  TrainingTone,
} from './surveys.types';
import { converters } from './surveys.converters';

interface GetSurveys {
  filters: Partial<SurveysFilters>;
}

interface GetSurvey {
  surveyId: string;
}

interface CreateSurvey {
  name: string;
  type: SurveyType;
}

interface UpdateSurvey {
  surveyId: string;
  data: Partial<{
    name: string;
    anonymous: boolean;
    hasWebAccess: boolean;
    notificationMethods: NotificationMethod[];
    scoringEnabled: boolean;
    minScore: number;
    retries: number;
    rewardPoints: number;
    publishDateTime: string | null;
    endDateTime: string | null;
    metadata: {
      withDescription: boolean;
      withImage: boolean;
      withAttachments: boolean;
      withVideo: boolean;
    };
  }>;
}

interface UpdateSurveyContent {
  surveyId: string;
  data: {
    translations: Translations<{
      title: string;
      description: string;
      imageId: string | null;
      attachmentIds: string[];
      videoUrls: string[];
    }>;
    questions: Array<{
      type: SurveyAnswerType;
      translations: Translations<{
        text: string;
        description: string;
        imageId: string | null;
        attachmentIds: string[];
        videoUrls: string[];
      }>;
      isRequired: boolean;
      answers: Array<{
        translations: Translations<{
          isCorrect: boolean;
          text: string;
          imageId: string | null;
        }>;
      }>;
      metadata: {
        withImage: boolean;
        withDescription: boolean;
        withAttachments: boolean;
        withVideo: boolean;
        choiceAnswerFormat: SurveyChoiceAnswerFormat;
        conditionalJumpRules: Array<{
          answerIndex: number;
          questionIndex: number;
        }>;
      };
    }>;
  };
}

interface ActivateSurvey {
  surveyId: string;
}

interface DeactivateSurvey {
  surveyId: string;
}

interface SendSurveyNotifications {
  surveyId: string;
}

interface CopySurvey {
  surveyId: string;
  name: string;
}

interface RemoveSurvey {
  surveyId: string;
}

interface GenerateTrainingFromPrompt {
  fileIds?: string[];
  name: string;
  language: Uppercase<ContentLanguage>;
  instructions: string;
  questionsNumber: number;
  questionType: SurveyChoiceAnswerType[];
  difficulty: TrainingDifficulty;
  tone: TrainingTone;
  contentPages: boolean;
}

interface GenerateTrainingFromImport {
  fileIds: string[];
  name: string;
}

interface GetSurveyGroupAssignments {
  surveyId: string;
}

interface UpdateSurveyGroupAssignments {
  surveyId: string;
  data: Partial<{ assign: string[]; unassign: string[] }>;
}

interface GetSurveyResults {
  surveyId: string;
}

interface ExportSurveyResults {
  surveyId: string;
}

interface ResendNotifications {
  surveyId: string;
  data: {
    methods: NotificationMethod[];
  };
}

interface GetSurveySubmissions {
  surveyId: string;
  filters: Partial<SurveySubmissionsFilters>;
}

interface GetSurveyPendingSubmissions {
  surveyId: string;
  filters: Partial<SurveyPendingSubmissionsFilters>;
}

interface GetSurveySubmission {
  surveyId: string;
  submissionId: string;
}

export const loaders = {
  getSurveys: ({ filters }: GetSurveys) => {
    return api<TableDataResponse<IBaseSurvey>>({
      url: Endpoints.clientAdmin.surveys.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        filter: {
          type: filters.type,
          status: filters.status,
          publishDateFrom: filters.publishedAt?.from,
          publishDateTo: filters.publishedAt?.to,
          anonymous: getBinaryValue(filters.anonymous),
          hasWebAccess: getBinaryValue(filters.hasWebAccess),
        },
      },
    });
  },
  getSurvey: ({ surveyId }: GetSurvey) => {
    return api<ISurvey>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).root,
    });
  },
  createSurvey: ({ name, type }: CreateSurvey) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.surveys.root,
      data: { name, type },
      method: 'post',
    });
  },
  generateTrainingFromPrompt: (data: GenerateTrainingFromPrompt) => {
    return api<{ jobId: string }>({
      url: Endpoints.clientAdmin.surveys.ai.trainingGeneration,
      data,
      method: 'post',
    });
  },
  generateTrainingFromImport: (data: GenerateTrainingFromImport) => {
    return api<{ jobId: string }>({
      url: Endpoints.clientAdmin.surveys.ai.trainingQuestionImport,
      data,
      method: 'post',
    });
  },
  updateSurvey: ({
    surveyId,
    data: { notificationMethods, ...data },
  }: UpdateSurvey) => {
    return api<IBaseSurvey>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).root,
      data: {
        ...data,
        emailNotificationsEnabled: notificationMethods?.includes('email'),
        mobileNotificationsEnabled:
          notificationMethods?.includes('appNotification'),
      },
      method: 'patch',
    });
  },
  updateSurveyContent: ({ surveyId, data }: UpdateSurveyContent) => {
    return api<IBaseSurvey>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).questions,
      data: {
        languages: data.translations,
        questions: data.questions.map(
          ({ isRequired, answers, ...question }) => ({
            ...question,
            isOptional: !isRequired,
            answerOptions: answers,
          }),
        ),
      },
      method: 'put',
    });
  },
  activateSurvey: ({ surveyId }: ActivateSurvey) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).activate,
      method: 'patch',
    });
  },
  deactivateSurvey: ({ surveyId }: DeactivateSurvey) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).deactivate,
      method: 'patch',
    });
  },
  sendSurveyNotifications: ({ surveyId }: SendSurveyNotifications) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).sendNotifications,
      method: 'post',
    });
  },
  copySurvey: async ({ surveyId, name }: CopySurvey) => {
    const currentSurvey = await loaders
      .getSurvey({ surveyId })
      .then(converters.getSurvey);

    const { data: createdSurvey } = await loaders.createSurvey({
      name,
      type: currentSurvey.type,
    });

    await loaders.updateSurvey({
      surveyId: createdSurvey.id,
      data: {
        anonymous: currentSurvey.anonymous,
        hasWebAccess: currentSurvey.hasWebAccess,
        notificationMethods: currentSurvey.notificationMethods,
        rewardPoints: currentSurvey.rewardPoints,
        metadata: currentSurvey.metadata,
        ...(currentSurvey.type === 'training'
          ? {
              scoringEnabled: currentSurvey.scoringEnabled,
              minScore: currentSurvey.minScore,
              retries: currentSurvey.retries,
            }
          : {}),
      },
    });

    return loaders.updateSurveyContent({
      surveyId: createdSurvey.id,
      data: {
        translations: currentSurvey.translations.map((trans) => ({
          language: trans.language,
          title: trans.title,
          description: trans.description,
          imageId: trans.imageId || null,
          attachmentIds: trans.attachments.map((attachment) => attachment.id),
          videoUrls: trans.videos,
        })),
        questions: currentSurvey.questions.map((question) => ({
          type: question.type,
          isRequired: question.isRequired,
          answers: question.options.map(({ index, ...option }) => ({
            ...option,
            translations: option.translations.map(
              ({ imageUrl, ...trans }) => trans,
            ),
          })),
          translations: question.translations.map((trans) => ({
            language: trans.language,
            text: trans.text,
            description: trans.description || '',
            imageId: trans.imageId || null,
            attachmentIds:
              trans.attachments.map((attachment) => attachment.id) || [],
            videoUrls: trans.videos,
          })),
          metadata: question.metadata,
        })),
      },
    });
  },
  deleteSurvey: ({ surveyId }: RemoveSurvey) => {
    return api({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).root,
      method: 'delete',
    });
  },
  getSurveyGroupAssignments: ({ surveyId }: GetSurveyGroupAssignments) => {
    return api<ISurveyGroupAssignment[]>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).groupAssignments,
    });
  },
  updateSurveyGroupAssignments: ({
    surveyId,
    data,
  }: UpdateSurveyGroupAssignments) => {
    return api({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).groupAssignments,
      data: { assign: data.assign, unassign: data.unassign },
      method: 'patch',
    });
  },
  getSurveyResults: ({ surveyId }: GetSurveyResults) => {
    return api<ISurveyResults>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).results,
    });
  },
  exportSurveyResults: ({ surveyId }: ExportSurveyResults) => {
    return api<{ jobId: string; queue: string }>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).exportToExcel,
      params: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  },
  resendNotifications: ({ surveyId, data }: ResendNotifications) => {
    return api({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).resendNotifications,
      data: {
        email: data.methods?.includes('email'),
        mobile: data.methods?.includes('appNotification'),
      },
      method: 'post',
    });
  },
  getSurveyPendingSubmissions: ({
    surveyId,
    filters,
  }: GetSurveyPendingSubmissions) => {
    const orgStructure = mergeOrgStructureIds(filters);

    return api<TableDataResponse<IBaseSurveyPendingSubmission>>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).submissions.pending,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search || undefined,
        ...getSorting(filters.sort),
        filter: {
          employeeIds: filters.employees,
          companyIds: orgStructure.companies,
          departmentIds: orgStructure.departments,
          teamIds: orgStructure.teams,
        },
      },
    });
  },
  getSurveySubmissions: ({ surveyId, filters }: GetSurveySubmissions) => {
    const orgStructure = mergeOrgStructureIds(filters);

    return api<TableDataResponse<IBaseSurveySubmission>>({
      url: Endpoints.clientAdmin.surveys.byId(surveyId).submissions.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search || undefined,
        ...getSorting(filters.sort),
        filter: {
          employeeIds: filters.employees,
          companyIds: orgStructure.companies,
          departmentsIds: orgStructure.departments,
          teamIds: orgStructure.teams,
          completionDateFrom: filters.completionDate?.from,
          completionDateTo: filters.completionDate?.to,
          scoreFrom: filters.score?.from,
          scoreTo: filters.score?.to,
        },
      },
    });
  },
  getSurveySubmission: ({ surveyId, submissionId }: GetSurveySubmission) => {
    return api<ISurveySubmission>({
      url: Endpoints.clientAdmin.surveys
        .byId(surveyId)
        .submissions.byId.replace(':submissionId', submissionId),
    });
  },
};
